import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useDeclineRequest } from "./use-decline-request";

// Sub-bucket 15 — port of /app/admin/mdf/requests/_components/DeclineRequestDialog.tsx.
// Reason is required + persisted to mdf_requests.declined_reason; the
// partner sees it in their MDF history. The service trims + caps at 1000.

export function DeclineRequestDialog({
  requestId,
  fundId,
  open,
  onOpenChange,
}: {
  requestId: string;
  fundId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const decline = useDeclineRequest();
  const [reason, setReason] = useState("");

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error("A decline reason is required so the partner knows why.");
      return;
    }
    if (trimmed.length > 1000) {
      toast.error("Reason is too long (max 1000 characters)");
      return;
    }
    decline.mutate(
      { id: requestId, fundId, reason: trimmed },
      {
        onSuccess: () => {
          onOpenChange(false);
          setReason("");
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to decline request")),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setReason("");
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Decline request
          </DialogTitle>
          <DialogDescription>
            The partner will see this reason in their MDF history.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-decline-reason">Reason</Label>
            <Textarea
              id="mdf-decline-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="e.g. Outside fund scope; please resubmit for Q4."
              required
            />
            <p className="text-[11px] text-muted-foreground">
              {reason.length}/1000
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={decline.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={decline.isPending || reason.trim().length === 0}
            >
              {decline.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Decline
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
